/**
 * Base Service Class
 * Abstract base class for all API services with common CRUD operations
 *
 * @module services/BaseService
 */

import apiClient from "./apiClient";
import { handleResponse } from "utils/responseHandler";

class BaseService {
  /**
   * Create a new service instance
   * @param {string} baseEndpoint - Base API endpoint (e.g., '/courses')
   */
  constructor(baseEndpoint) {
    this.baseEndpoint = baseEndpoint;
    this.client = apiClient;
  }

  /**
   * Get all items
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getAll(params = {}) {
    return handleResponse(
      this.client.get(this.baseEndpoint, { params })
    );
  }

  /**
   * Get single item by ID
   * @param {string} id - Item ID
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getById(id, params = {}) {
    return handleResponse(
      this.client.get(`${this.baseEndpoint}/${id}`, { params })
    );
  }

  /**
   * Create new item
   * @param {Object} data - Item data
   * @returns {Promise} API response
   */
  async create(data) {
    return handleResponse(
      this.client.post(this.baseEndpoint, data)
    );
  }

  /**
   * Update existing item
   * @param {string} id - Item ID
   * @param {Object} data - Updated item data
   * @returns {Promise} API response
   */
  async update(id, data) {
    return handleResponse(
      this.client.put(`${this.baseEndpoint}/${id}`, data)
    );
  }

  /**
   * Partially update existing item
   * @param {string} id - Item ID
   * @param {Object} data - Partial item data
   * @returns {Promise} API response
   */
  async patch(id, data) {
    return handleResponse(
      this.client.patch(`${this.baseEndpoint}/${id}`, data)
    );
  }

  /**
   * Delete item
   * @param {string} id - Item ID
   * @returns {Promise} API response
   */
  async delete(id) {
    return handleResponse(
      this.client.delete(`${this.baseEndpoint}/${id}`)
    );
  }

  /**
   * Get paginated items
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response
   */
  async getPaginated(page = 1, limit = 12, params = {}) {
    return handleResponse(
      this.client.get(this.baseEndpoint, {
        params: { page, limit, ...params },
      })
    );
  }

  /**
   * Search items
   * @param {string} query - Search query
   * @param {Object} params - Additional parameters
   * @returns {Promise} API response
   */
  async search(query, params = {}) {
    return handleResponse(
      this.client.get(`${this.baseEndpoint}/search`, {
        params: { q: query, ...params },
      })
    );
  }

  /**
   * Custom GET request
   * @param {string} path - Endpoint path (relative to baseEndpoint)
   * @param {Object} params - Query parameters
   * @param {Object} [config={}] - Axios request configuration
   * @returns {Promise} API response
   */
  async get(path, params = {}, config = {}) {
    const endpoint = path.startsWith("/")
      ? path
      : `${this.baseEndpoint}/${path}`;
    return handleResponse(this.client.get(endpoint, { params, ...config }));
  }

  /**
   * Custom POST request
   * @param {string} path - Endpoint path (relative to baseEndpoint)
   * @param {Object} data - Request body
   * @param {Object} [config={}] - Axios request configuration
   * @returns {Promise} API response
   */
  async post(path, data, config = {}) {
    const endpoint = path.startsWith("/")
      ? path
      : `${this.baseEndpoint}/${path}`;
    return handleResponse(this.client.post(endpoint, data, config));
  }

  /**
   * Custom PUT request
   * @param {string} path - Endpoint path (relative to baseEndpoint)
   * @param {Object} data - Request body
   * @param {Object} [config={}] - Axios request configuration
   * @returns {Promise} API response
   */
  async put(path, data, config = {}) {
    const endpoint = path.startsWith("/")
      ? path
      : `${this.baseEndpoint}/${path}`;
    return handleResponse(this.client.put(endpoint, data, config));
  }

  /**
   * Custom DELETE request
   * @param {string} path - Endpoint path (relative to baseEndpoint)
   * @param {Object} [config={}] - Axios request configuration
   * @returns {Promise} API response
   */
  async deleteCustom(path, config = {}) {
    const endpoint = path.startsWith("/")
      ? path
      : `${this.baseEndpoint}/${path}`;
    return handleResponse(this.client.delete(endpoint, config));
  }
}

export default BaseService;
